import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Headphones, Volume2, VolumeX, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useLiveCoach, CAT_COLOR, LEVELS } from "@/context/LiveCoachContext";
import { cn } from "@/lib/utils";

export function LiveCoachOverlay() {
    const { enabled, setEnabled, current, history, level, voice, setVoice, loading, monitoring, active } = useLiveCoach();
    const [expanded, setExpanded] = useState(false);
    if (!enabled) return null;

    const color = current ? (CAT_COLOR[current.category] || "#00C2FF") : "#00C2FF";

    return (
        <div className="fixed bottom-4 right-4 z-50 w-[330px] max-w-[calc(100vw-2rem)]" data-testid="live-coach-overlay">
            <div className="bg-surface2/95 backdrop-blur-md border border-border rounded-sm shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border bg-black/40">
                    <div className="flex items-center gap-2">
                        <span className="relative grid place-items-center">
                            <Headphones className="w-4 h-4 text-volt" />
                        </span>
                        <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-white">Live Coach</span>
                        <span className={cn("w-1.5 h-1.5 rounded-full", active ? "bg-success ww-live-dot" : "bg-warn")} />
                        <span className="text-[10px] font-mono uppercase" style={{ color: active ? "#00E676" : "#FFB000" }}>{active ? "Live" : "Armed"}</span>
                        <span className="text-[10px] font-mono text-muted-foreground uppercase">· {LEVELS[level]?.label}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setVoice(!voice)} data-testid="overlay-voice-toggle"
                            className="grid place-items-center w-7 h-7 rounded-sm hover:bg-white/10 text-muted-foreground hover:text-white" title={voice ? "Mute voice" : "Enable voice"}>
                            {voice ? <Volume2 className="w-4 h-4 text-volt" /> : <VolumeX className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setExpanded((e) => !e)} data-testid="overlay-expand"
                            className="grid place-items-center w-7 h-7 rounded-sm hover:bg-white/10 text-muted-foreground hover:text-white">
                            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                        </button>
                        <button onClick={() => setEnabled(false)} data-testid="overlay-close"
                            className="grid place-items-center w-7 h-7 rounded-sm hover:bg-alert/15 text-muted-foreground hover:text-alert">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="px-3.5 py-4 min-h-[78px] flex items-center">
                    <AnimatePresence mode="wait">
                        {!monitoring ? (
                            <div className="flex items-center gap-2 text-muted-foreground text-sm" data-testid="overlay-waiting">
                                <span className="w-2 h-2 rounded-full bg-warn" />
                                Waiting for live gameplay — start monitoring to begin coaching.
                            </div>
                        ) : current ? (
                            <motion.div key={current.key} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
                                transition={{ duration: 0.28 }} className="w-full">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm" style={{ color, background: `${color}1a`, border: `1px solid ${color}55` }}>
                                        {current.category}
                                    </span>
                                    {current.priority === "high" && <span className="text-[9px] font-mono uppercase text-alert">● Priority</span>}
                                </div>
                                <div className="text-[15px] leading-snug text-white font-medium">"{current.text}"</div>
                            </motion.div>
                        ) : (
                            <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                {loading ? <Loader2 className="w-4 h-4 animate-spin text-volt" /> : <Headphones className="w-4 h-4" />}
                                {loading ? "Analyzing your play..." : "Listening — first callout incoming."}
                            </div>
                        )}
                    </AnimatePresence>
                </div>

                {expanded && (
                    <div className="border-t border-border px-3.5 py-3 max-h-52 overflow-y-auto space-y-2" data-testid="overlay-history">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Recent Callouts</div>
                        {history.length === 0 && <div className="text-xs text-muted-foreground">No callouts yet.</div>}
                        {history.map((c) => (
                            <div key={c.key} className="flex items-start gap-2 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: CAT_COLOR[c.category] || "#00C2FF" }} />
                                <span className="text-foreground/80">{c.text}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
